import React, { useState } from 'react'
import type { SetupClient } from '../../../api/setupClient'
import {
  StepContainer,
  StepHeading,
  StepDescription,
  ErrorAlert,
  FieldGroup,
  FieldLabel,
  FieldInput,
  ButtonRow,
  PrimaryButton,
} from '../setupStyled'

interface WelcomeProps {
  client: SetupClient
  onNext: () => void
}

/**
 * First step of the one-time admin setup wizard: the operator proves
 * possession of the setup token (emitted once by the boot banner /
 * non-interactive init — see setup_token.go's GenerateSetupToken) before
 * anything else can happen. This is the ONLY step that authenticates —
 * every later step reuses the token this step captures via
 * `client.begin(token)`.
 *
 * OIDCPAM Gate-6 Phase 2b: migrated off MUI Box/TextField/Button onto a
 * minimal local themed form (no @devnw/forms text-input exists yet — see
 * setupStyled.ts's module comment); the data-fetching/state machine below
 * is unchanged from the pre-migration version.
 */
export const Welcome = ({ client, onNext }: WelcomeProps): JSX.Element => {
  const [token, setToken] = useState('')
  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  const handleNext = async (): Promise<void> => {
    setStatus('working')
    setError(null)
    try {
      await client.begin(token)
      setStatus('idle')
      onNext()
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : 'Failed to verify the setup token'
      )
      setStatus('error')
    }
  }

  return (
    <StepContainer>
      <StepHeading>Welcome</StepHeading>
      <StepDescription>
        This one-time wizard creates the first administrator account for this
        deployment. It can only be run once — enter the setup token shown on
        the operator console (or emitted at first boot) to continue.
      </StepDescription>
      {status === 'error' && error && <ErrorAlert role="alert">{error}</ErrorAlert>}
      <FieldGroup>
        <FieldLabel htmlFor="setup-token">Setup token</FieldLabel>
        <FieldInput
          id="setup-token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          autoComplete="off"
        />
      </FieldGroup>
      <ButtonRow>
        <PrimaryButton
          type="button"
          disabled={!token || status === 'working'}
          onClick={() => handleNext()}
          aria-busy={status === 'working' || undefined}
        >
          {status === 'working' ? 'Working…' : 'Next'}
        </PrimaryButton>
      </ButtonRow>
    </StepContainer>
  )
}
